import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  createContext
} from 'react';
import { useHistory } from 'react-router-dom';
import deepmerge from 'deepmerge';

import { Logger } from 'wtb-utils';
import { useAppContext, useTransactionContext } from 'context';
import { APP_STEPS } from 'constants.js';

const DialogNavigationContext = createContext();

const defaultState = {
  step: null,
  hasBack: true,
  hasClose: true,
  hasFooter: true,
  overrideClose: false,
  isInvisible: false,
  onClose: () => {}
};

const DialogNavigationProvider = ({ children, initialState: state = {} }) => {
  const initialState = deepmerge(defaultState, state);

  const history = useHistory();

  const { setAppStep } = useAppContext();
  const { transaction: { id }, selectedPaymentPlan } = useTransactionContext();

  const [ title, setTitle ] = useState(initialState.title);
  const [ back, setBack ] = useState(initialState.back);
  const [ hasBack, setHasBack ] = useState(initialState.hasBack);
  const [ hasClose, setHasClose ] = useState(initialState.hasClose);
  const [ hasFooter, setHasFooter ] = useState(initialState.hasFooter);
  const [ onClose, setOnClose ] = useState(initialState.onClose);
  // Takes precedence over onClose; parent routes never set it, so they can't overwrite it.
  const [ onCloseOverride, setOnCloseOverride ] = useState(null);
  const [ overrideClose, setOverrideClose ] = useState(initialState.overrideClose);
  const [ isInvisible, setIsInvisible ] = useState(initialState.isInvisible);

  // Preceded by underscore to show that it's intended to be used mostly internally.
  const _close = () => {
    setAppStep(APP_STEPS.PAYMENT);
  };

  const close = () => {
    if (onCloseOverride && typeof onCloseOverride === 'function') {
      onCloseOverride();
      return;
    }

    if (onClose && typeof onClose === 'function') {
      onClose();
    }

    if (overrideClose) return;

    _close();
  };

  useEffect(() => {
    if (!selectedPaymentPlan) {
      setBack(null);
    }
  }, [ selectedPaymentPlan ]);

  const setPath = useCallback((path) => {
    if (!id) {
      Logger.error('An id is needed before changing step');
      return;
    }
    if (!path) {
      Logger.error('A path is needed before changing step');
      return;
    }

    history.push(`/${id}/${path}`);
  }, [ id, history ]);

  const _setOnClose = useCallback((func) => {
    setOnClose(() => func);
  }, []);

  const _setOnCloseOverride = useCallback((func) => {
    setOnCloseOverride(() => func);
  }, []);

  const _setBack = useCallback((func) => {
    setBack(() => func);
  }, []);

  return (
    <DialogNavigationContext.Provider
      value={{
        title, setTitle,
        back, setBack: _setBack,
        hasBack, setHasBack,
        hasClose, setHasClose,
        hasFooter, setHasFooter,
        onClose, setOnClose: _setOnClose,
        setOnCloseOverride: _setOnCloseOverride,
        overrideClose, setOverrideClose,
        isInvisible, setIsInvisible,
        close, _close,
        setDialogNavigationPath: setPath
      }}
    >
      {children}
    </DialogNavigationContext.Provider>
  );
};

const DialogNavigationConsumer = DialogNavigationContext.Consumer;

const useDialogNavigationContext = () => {
  const context = useContext(DialogNavigationContext);

  if (context === undefined) {
    throw new Error('useDialogNavigationContext must be used within a DialogNavigationProvider');
  }

  return context;
};

export {
  DialogNavigationContext, DialogNavigationProvider, DialogNavigationConsumer, useDialogNavigationContext
};
